import { Script } from 'gatsby';
import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import StructuredData from 'components/StructuredData';
import {
  structuredOrgData,
  structuredFaq,
} from 'components/StructuredData/structuredDataHelpers';
import React from 'react';
import { Helmet } from 'react-helmet';

import { FaqSchema } from 'utils/types';

const config = require('../../utils/constants/siteConfig');

/** Datadog initialization */
if (process.env.NODE_ENV !== 'test') {
  const ddConfig = {
    clientToken: process.env.DATADOG_CLIENT_TOKEN || '',
    site: 'datadoghq.com',
    service: 'www',
    env: process.env.REACT_APP_ENV,
    sampleRate: 100,
    silentMultipleInit: true,
  };

  datadogRum.init({
    ...ddConfig,
    applicationId: process.env.DATADOG_APPLICATION_ID || '',
    premiumSampleRate: 0,
    trackInteractions: true,
  });

  datadogLogs.init({
    ...ddConfig,
    forwardErrorsToLogs: true,
    telemetrySampleRate: 0,
  });
}

interface Props {
  title?: string;
  description?: string;
  keywords?: string;
  url?: string;
  faqSchema?: FaqSchema;
  slug?: string;
  canonicalUrl?: string;
  noIndex?: boolean;
}

export const SEO = ({
  title,
  description,
  keywords,
  url,
  faqSchema,
  canonicalUrl,
  noIndex,
}: Props): JSX.Element => {
  return (
    <>
      <Helmet htmlAttributes={{ lang: 'en' }}>
        {/* Level Access Script */}
        <script
          type="text/javascript"
          src="https://cdn.levelaccess.net/accessjs/YW1wMTMwOTk/access.js"
          defer
        ></script>

        {/* Start VWO Async SmartCode */}
        <script data-cfasync="false" type="text/javascript">
          {`
        window._vwo_code = window._vwo_code || (function(){
        var account_id=533964,
        settings_tolerance=2000,
        library_tolerance=2500,
        use_existing_jquery=false,
        is_spa=1,
        hide_element='body',

        /* DO NOT EDIT BELOW THIS LINE */
        f=false,d=document,code={use_existing_jquery:function(){return use_existing_jquery;},library_tolerance:function(){return library_tolerance;},finish:function(){if(!f){f=true;var a=d.getElementById('_vis_opt_path_hides');if(a)a.parentNode.removeChild(a);}},finished:function(){return f;},load:function(a){var b=d.createElement('script');b.src=a;b.type='text/javascript';b.innerText;b.onerror=function(){_vwo_code.finish();};d.getElementsByTagName('head')[0].appendChild(b);},init:function(){
        window.settings_timer=setTimeout(function () {_vwo_code.finish() },settings_tolerance);var a=d.createElement('style'),b=hide_element?hide_element+'{opacity:0 !important;filter:alpha(opacity=0) !important;background:none !important;}':'',h=d.getElementsByTagName('head')[0];a.setAttribute('id','_vis_opt_path_hides');a.setAttribute('type','text/css');if(a.styleSheet)a.styleSheet.cssText=b;else a.appendChild(d.createTextNode(b));h.appendChild(a);this.load('https://dev.visualwebsiteoptimizer.com/j.php?a='+account_id+'&u='+encodeURIComponent(d.URL)+'&f='+(+is_spa)+'&r='+Math.random());return settings_timer; }};window._vwo_settings_timer = code.init(); return code; }());
      `}
        </script>

        {/* This is the Mixpanel integration with VWO.
      It needs to be included on the page if the Experiment is running
      on a particular page */}
        <script type="text/javascript">
          {`
        var dataSendingTimer;
        var tryPushingDataTimer;
        var _vis_data = {};

        window._CURRENT_CAMPAIGNS_EW_VWO = [];
        window.VWO = window.VWO || [];

        window.VWO.push([
          "onVariationApplied",
          function (data) {
            if (!data) return;

            var expId = data[1];
            var variationId = data[2];

            var isExperimentDefined =
              typeof _vwo_exp[expId].comb_n[variationId] != "undefined";
            var isEventVisualExperiment =
              ["VISUAL_AB", "VISUAL", "SPLIT_URL", "SURVEY"].indexOf(
                _vwo_exp[expId].type
              ) > -1;

            if (isExperimentDefined && isEventVisualExperiment) {
              var test = "VWO-Test-ID-" + expId;
              var variant = _vwo_exp[expId].comb_n[variationId];

              window._CURRENT_CAMPAIGNS_EW_VWO.push({
                test: test,
                variant: variant,
              });
            }
          },
        ]);
      `}
        </script>
        {/* CookiePro Cookies Consent Notice start for http://everlywell.com */}
        <script
          src="https://cookie-cdn.cookiepro.com/scripttemplates/otSDKStub.js"
          type="text/javascript"
          // @ts-ignore
          charset="UTF-8"
          data-domain-script="657473ed-a2c5-4d12-91d8-042d30fe370a"
        ></script>
        <script type="text/javascript">function OptanonWrapper() {}</script>
        {/* CookiePro Cookies Consent Notice end for http://everlywell.com */}
        {/* General tags */}
        <title>{title || config.title}</title>
        <meta
          name="description"
          content={description || config.siteDescription}
        />
        <meta name="keywords" content={keywords || config.keywords} />
        {/* Canonical URL is a Contentful field available in the Pages model.
        If the field is blank, Gastby Helmet plugin will take over and generate the
        Canonical URL based on the page slug.
        */}
        {canonicalUrl ? (
          <link
            rel="canonical"
            key={`${config.siteUrl}/${canonicalUrl}/`}
            href={`${config.siteUrl}/${canonicalUrl}/`}
          />
        ) : (
          []
        )}
        {/* OpenGraph tags */}
        <meta property="og:title" content={title || config.title} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={url || config.siteUrl} />
        <meta
          property="og:description"
          content={description || config.siteDescription}
        />
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:creator"
          content={config.userTwitter ? config.userTwitter : ''}
        />
        <meta name="twitter:title" content={title || config.siteTitle} />
        <meta
          name="twitter:description"
          content={description || config.siteDescription}
        />
        {/* Disable iPhone auto zoom on select drop down menus */}
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1"
        ></meta>
        {noIndex ? (
          <meta name="robots" content="noindex, nofollow, noimageindex" />
        ) : null}
      </Helmet>
      {/* Schema.org tags */}
      {/* Organization Schema Attributes from config */}
      <StructuredData data={structuredOrgData} />
      {/* FAQ Schema Attributes from Contentful */}
      {faqSchema?.length ? (
        <StructuredData data={structuredFaq(faqSchema)} />
      ) : null}
    </>
  );
};
